import React, { FC, useCallback, useState } from 'react'
import styles from 'styles/components/card.module.scss'
import modal from 'styles/components/modal.module.scss'
import text from 'styles/utility/text.module.scss'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { FlexRow } from 'components/Flex'
import { HorizontalLine, Spacer } from 'components/Utility'
import { CloseButton, OrangeButton } from 'components/Button'
import { BasicCard } from 'components/Card'
import { closeModal, openConfirmModal } from 'reducks/modal/operations'
import { ConfirmModal } from 'components/Modal'
import { State } from 'index'
import { getRole, getStoreRef } from 'reducks/auth/selectors'
import { Role } from 'lib/role'
import { getStocks } from 'reducks/stock/selectors'
import { getBulkStockOpen, getBulkStockDate } from 'reducks/modal/selectors'
import { saveBulkStocksAmount } from 'reducks/stock/operations'

const BulkStockModal: FC = () => {
  const dispatch = useDispatch()
  const open = useSelector((state: State) => getBulkStockOpen(state))
  const date = useSelector((state: State) => getBulkStockDate(state))
  const stocks = useSelector((state: State) => getStocks(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const role = useSelector((state: State) => getRole(state))
  const roleManager = new Role(role, storeRef)

  const [amount, setAmount] = useState<number>(0)
  const [hasEdited, setHasEdited] = useState<boolean>(false)
  const handleAmount = useCallback(
    (value: number) => {
      setAmount((prevState) => prevState + value)
      setHasEdited(true)
    },
    [setAmount, setHasEdited]
  )

  const confirmCloseModal = useCallback(() => {
    if (hasEdited) {
      dispatch(openConfirmModal())
    } else {
      dispatch(closeModal())
    }
  }, [hasEdited])

  return (
    <div className={open ? modal.open : ''}>
      <div className={modal.overlay}>
        <div className={modal.overlay__background} onClick={confirmCloseModal} />
        {open && (
          <div className={modal.overlay__container}>
            <BasicCard buttonComponent={<CloseButton onClick={confirmCloseModal} />} title={''}>
              <p className={classNames(text.font_bold, text.font_large)}>販売中の商品の在庫を一括で変更します。</p>
              <HorizontalLine verticalMargin={20} />
              <FlexRow justifyContent={'center'}>
                <button className={styles.row__button} disabled={amount <= 0} onClick={() => handleAmount(-1)}>
                  <img
                    src={amount <= 0 ? '/icon/grey/minus_28x29.svg' : '/icon/orange/minus.svg'}
                    alt={'在庫を減らす'}
                  />
                </button>
                <Spacer size={24} axis={'horizontal'} />
                <span
                  className={classNames(text.font_xxlarge, {
                    [text.color_orange_main]: amount === 0,
                    [text.font_bold]: amount === 0,
                  })}
                >
                  {amount.toLocaleString()}
                </span>
                <Spacer size={24} axis={'horizontal'} />
                <button className={styles.row__button}>
                  <img src={'/icon/orange/plus.svg'} alt={'在庫を増やす'} onClick={() => handleAmount(1)} />
                </button>
              </FlexRow>
              <HorizontalLine verticalMargin={12} />
              <Spacer size={12} />
              <OrangeButton
                disabled={!roleManager.hasEnoughRole(5)}
                label={'在庫を一括で設定する'}
                onClick={async () => {
                  await dispatch(saveBulkStocksAmount(stocks, amount, date))
                  dispatch(closeModal())
                }}
                size={'medium'}
                type={'main'}
              />
              <Spacer size={16} />
              <p className={classNames(text.font_small, text.font_bold, text.color_grey_01)}>
                本日分の販売を終了する場合は、0で設定して下さい
                <br />
                この操作は取り消しできないため、注意して実行して下さい
              </p>
            </BasicCard>
          </div>
        )}
        <ConfirmModal
          description={'在庫の変更が完了していません。編集した内容が破棄されますが、よろしいでしょうか？'}
          title={'編集内容の破棄'}
          onClose={() => {
            setAmount(0)
            setHasEdited(false)
          }}
        />
      </div>
    </div>
  )
}

export default BulkStockModal
